import type { UploadEvidenceData } from 'api';
import { logger } from 'monitor';
import { create } from 'zustand';

import { extractExifData } from '../utils/exif/exif';

const DB_NAME = 'offlineEvidenceDB';
const DB_VERSION = 1;
const METADATA_STORE = 'evidenceMetadata';
const FILES_STORE = 'evidenceFiles';

interface OfflineEvidenceFile extends Omit<UploadEvidenceData, 'photo' | 'onProgress'> {
  offlineId: string;
  photoMeta: {
    name: string;
    size: number;
    type: string;
    lastModified: number;
  };
}

const generateOfflineId = (file: UploadEvidenceData): string =>
  `${file.photo.name}-${file.photo.size}-${file.startTime}-${Date.now()}`.replace(/\s+/g, '').trim();

const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(METADATA_STORE)) {
        db.createObjectStore(METADATA_STORE, { keyPath: 'offlineId' });
      }

      if (!db.objectStoreNames.contains(FILES_STORE)) {
        db.createObjectStore(FILES_STORE, { keyPath: 'offlineId' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      logger.error('Failed to open IndexedDB', { error: request.error });
      reject(request.error);
    };
  });
};

const storeEvidenceFile = async (metadata: OfflineEvidenceFile, file: File): Promise<void> => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

    const metadataStore = transaction.objectStore(METADATA_STORE);
    const metadataRequest = metadataStore.put(metadata);

    metadataRequest.onerror = () => {
      logger.error('Failed to store evidence metadata', { error: metadataRequest.error });
      reject(metadataRequest.error);
    };

    const filesStore = transaction.objectStore(FILES_STORE);
    const fileRequest = filesStore.put({ file, offlineId: metadata.offlineId });

    fileRequest.onerror = () => {
      logger.error('Failed to store evidence file', { error: fileRequest.error });
      reject(fileRequest.error);
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => {
      logger.error('Transaction failed', { error: transaction.error });
      reject(transaction.error);
    };
  });
};

const getAllEvidenceMetadata = async (): Promise<OfflineEvidenceFile[]> => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([METADATA_STORE], 'readonly');
    const store = transaction.objectStore(METADATA_STORE);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => {
      logger.error('Failed to get evidence metadata', { error: request.error });
      reject(request.error);
    };
  });
};

const getFileById = async (offlineId: string): Promise<File | null> => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([FILES_STORE], 'readonly');
    const store = transaction.objectStore(FILES_STORE);
    const request = store.get(offlineId);

    request.onsuccess = () => {
      if (request.result) {
        resolve(request.result.file);
      } else {
        resolve(null);
      }
    };

    request.onerror = () => {
      logger.error('Failed to get file', { error: request.error });
      reject(request.error);
    };
  });
};

const removeEvidenceFile = async (offlineId: string): Promise<void> => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

    const metadataStore = transaction.objectStore(METADATA_STORE);
    metadataStore.delete(offlineId);

    const filesStore = transaction.objectStore(FILES_STORE);
    filesStore.delete(offlineId);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => {
      logger.error('Failed to remove evidence file', { error: transaction.error });
      reject(transaction.error);
    };
  });
};

const clearAllEvidenceFiles = async (): Promise<void> => {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([METADATA_STORE, FILES_STORE], 'readwrite');

    const metadataStore = transaction.objectStore(METADATA_STORE);
    metadataStore.clear();

    const filesStore = transaction.objectStore(FILES_STORE);
    filesStore.clear();

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => {
      logger.error('Failed to clear evidence files', { error: transaction.error });
      reject(transaction.error);
    };
  });
};

interface PhotoWithObjectUrl {
  id: string;
  url: string;
}

interface StoreState {
  evidenceFiles: OfflineEvidenceFile[];
  objectUrls: Record<string, PhotoWithObjectUrl[]>;

  addEvidenceFile: (file: UploadEvidenceData) => Promise<string>;
  getEvidenceFiles: () => OfflineEvidenceFile[];
  getEvidenceFileById: (offlineId: string) => OfflineEvidenceFile | undefined;
  getEvidenceFilesByParentId: (parentId: string) => OfflineEvidenceFile[];
  getPhotoFromOfflineFile: (offlineFile: OfflineEvidenceFile) => Promise<File | null>;
  getPhotoObjectUrlsByParentId: (parentId: string) => Promise<PhotoWithObjectUrl[]>;
  removeEvidenceFile: (offlineId: string) => Promise<void>;
  clearEvidenceFiles: () => Promise<void>;
  cleanupObjectUrls: (parentId: string) => void;
  initializeStore: () => Promise<void>;
}

export const useOfflineEvidenceStore = create<StoreState>((set, get) => {
  getAllEvidenceMetadata().then((metadata) => set({ evidenceFiles: metadata }));

  return {
    addEvidenceFile: async (file: UploadEvidenceData) => {
      try {
        const offlineId = generateOfflineId(file);
        const exifData = await extractExifData(file.photo);

        const metadata: OfflineEvidenceFile = {
          evidenceType: file.evidenceType,
          metadata: { ...file.metadata, taken_at: exifData.timestamp },
          model: file.model,
          offlineId,
          parentId: file.parentId,
          photoMeta: {
            lastModified: file.photo.lastModified,
            name: file.photo.name,
            size: file.photo.size,
            type: file.photo.type,
          },
          startTime: file.startTime,
        };

        await storeEvidenceFile(metadata, file.photo);

        set((state: StoreState) => ({
          evidenceFiles: [...state.evidenceFiles, metadata],
        }));

        const parentId = String(file.parentId);

        const photo = await getFileById(offlineId);
        if (photo) {
          const url = URL.createObjectURL(photo);
          const newPhotoUrl = { id: offlineId, url };

          const existingUrls = get().objectUrls[parentId] || [];

          set((state: StoreState) => ({
            objectUrls: {
              ...state.objectUrls,
              [parentId]: [...existingUrls, newPhotoUrl],
            },
          }));
        }

        return offlineId;
      } catch (error) {
        logger.error('Failed to add evidence file', { error });
        throw error;
      }
    },

    cleanupObjectUrls: (parentId: string) => {
      const urls = get().objectUrls[parentId] || [];

      urls.forEach((photo) => {
        URL.revokeObjectURL(photo.url);
      });

      set((state: StoreState) => {
        const { [parentId]: _, ...rest } = state.objectUrls;
        return { objectUrls: rest };
      });
    },

    clearEvidenceFiles: async () => {
      try {
        await clearAllEvidenceFiles();

        Object.keys(get().objectUrls).forEach(get().cleanupObjectUrls);

        set({ evidenceFiles: [], objectUrls: {} });
      } catch (error) {
        logger.error('Failed to clear evidence files', { error });
        throw error;
      }
    },

    evidenceFiles: [],

    getEvidenceFileById: (offlineId: string) => get().evidenceFiles.find((file) => file.offlineId === offlineId),

    getEvidenceFiles: () => get().evidenceFiles,

    getEvidenceFilesByParentId: (parentId: string) =>
      get().evidenceFiles.filter((file) => String(file.parentId) === String(parentId)),

    getPhotoFromOfflineFile: async (offlineFile: OfflineEvidenceFile) => {
      try {
        return await getFileById(offlineFile.offlineId);
      } catch (error) {
        logger.error('Failed to get photo from offline file', {
          error,
          fileName: offlineFile.photoMeta.name,
        });
        return null;
      }
    },

    getPhotoObjectUrlsByParentId: async (parentId: string) => {
      const stringParentId = String(parentId);

      const offlineFiles = get().getEvidenceFilesByParentId(stringParentId);

      const existingUrls = get().objectUrls[stringParentId] || [];
      const existingIds = existingUrls.map((url) => url.id);

      const newFiles = offlineFiles.filter((file) => !existingIds.includes(file.offlineId));

      if (newFiles.length === 0 && existingUrls.length === offlineFiles.length && existingUrls.length > 0) {
        return existingUrls;
      }

      const newPhotoUrls = await Promise.all(
        newFiles.map(async (file) => {
          const photo = await get().getPhotoFromOfflineFile(file);
          if (photo) {
            return {
              id: file.offlineId,
              url: URL.createObjectURL(photo),
            };
          }
          return null;
        })
      );

      const validNewPhotoUrls = newPhotoUrls.filter(Boolean) as PhotoWithObjectUrl[];

      const allPhotoUrls = offlineFiles
        .map((file) => {
          const newUrl = validNewPhotoUrls.find((url) => url.id === file.offlineId);
          if (newUrl) return newUrl;

          const existingUrl = existingUrls.find((url) => url.id === file.offlineId);
          if (existingUrl) return existingUrl;

          logger.warn('Missing URL for offline file', {
            fileName: file.photoMeta.name,
            offlineId: file.offlineId,
          });
          return null;
        })
        .filter(Boolean) as PhotoWithObjectUrl[];

      logger.info('Generated photo URLs', {
        count: allPhotoUrls.length,
        existingUrlsCount: existingUrls.length,
        newUrlsCount: validNewPhotoUrls.length,
        offlineFilesCount: offlineFiles.length,
        parentId: stringParentId,
      });

      set((state: StoreState) => ({
        objectUrls: {
          ...state.objectUrls,
          [stringParentId]: allPhotoUrls,
        },
      }));

      return allPhotoUrls;
    },

    initializeStore: async () => {
      try {
        const metadata = await getAllEvidenceMetadata();
        set({ evidenceFiles: metadata });
      } catch (error) {
        logger.error('Failed to initialize store', { error });
        set({ evidenceFiles: [] });
      }
    },

    objectUrls: {},

    removeEvidenceFile: async (offlineId: string) => {
      try {
        const file = get().getEvidenceFileById(offlineId);
        if (!file) return;

        const parentId = String(file.parentId);
        await removeEvidenceFile(offlineId);

        set((state: StoreState) => ({
          evidenceFiles: state.evidenceFiles.filter((f) => f.offlineId !== offlineId),
        }));

        const urls = get().objectUrls[parentId];
        if (!urls) return;

        const url = urls.find((u) => u.id === offlineId);
        if (url) {
          URL.revokeObjectURL(url.url);

          set((state: StoreState) => ({
            objectUrls: {
              ...state.objectUrls,
              [parentId]: urls.filter((u) => u.id !== offlineId),
            },
          }));
        }
      } catch (error) {
        logger.error('Failed to remove evidence file', { error });
        throw error;
      }
    },
  };
});
